/**
 * Del Puerto — Optimistic cart drawer updates
 *
 * Goal: when the user taps +, −, or the remove button on a line in the
 * cart drawer, the visible numbers (line total, subtotal, progress bar,
 * cart icon bubble) update IMMEDIATELY in JS, before the server round-
 * trip completes. When Shopify's cart.js fires its real `/cart/change.js`
 * request and replaces the drawer DOM with server-rendered HTML, the
 * values should already match, so there is no visible flicker.
 *
 * On error or mismatch, the innerHTML replacement from Shopify reconciles
 * us back to server truth. The only visual artifact in that case is a
 * brief snap to the correct value — far better than a 400–800 ms freeze.
 *
 * This script is intentionally read-only with respect to the cart state
 * on the server: it never fires its own requests. Dawn's existing flow
 * still does all the network work; we just paint ahead of it.
 *
 * Scope: cart drawer only. The cart page (`/cart`) uses a different
 * render path and is not a primary surface on mobile for D2C.
 */

(function() {
  'use strict';

  // ---- Config -------------------------------------------------------------
  // Free-delivery threshold in cents. Must stay in sync with the value in
  // snippets/free-delivery-bar.liquid (threshold_cents = 150000).
  var FREE_DELIVERY_THRESHOLD_CENTS = 150000;

  // ---- Money helpers ------------------------------------------------------
  // Detect the shop's money format by looking at the first element that
  // already has a server-rendered price. This avoids hardcoding a locale
  // that might diverge from shop settings.
  var _moneyFormatCache = null;

  function detectMoneyFormat() {
    if (_moneyFormatCache) return _moneyFormatCache;

    var sample = document.querySelector('.cart-drawer .dp-totals__value, .cart-drawer .totals__total-value');
    var raw = sample ? sample.textContent.trim() : '';

    // Default fallback: ₱1,234.00
    var hasDecimals = /[.,]\d{2}\b/.test(raw);
    var hasSuffix = / [A-Z]{3}$/.test(raw); // " PHP"
    var suffix = hasSuffix ? raw.slice(raw.length - 4) : '';

    _moneyFormatCache = { hasDecimals: hasDecimals, suffix: suffix };
    return _moneyFormatCache;
  }

  function formatCents(cents, opts) {
    opts = opts || {};
    var fmt = detectMoneyFormat();
    var pesos = cents / 100;
    var useDecimals = (opts.forceDecimals != null) ? opts.forceDecimals : fmt.hasDecimals;
    var formatted = pesos.toLocaleString('en-PH', {
      minimumFractionDigits: useDecimals ? 2 : 0,
      maximumFractionDigits: useDecimals ? 2 : 0
    });
    var result = '\u20B1' + formatted; // ₱
    if (opts.withSuffix !== false && fmt.suffix) result += fmt.suffix;
    return result;
  }

  function parseCents(str) {
    if (!str) return 0;
    // Strip everything except digits and a decimal point.
    var cleaned = String(str).replace(/[^\d.]/g, '');
    if (!cleaned) return 0;
    var pesos = parseFloat(cleaned);
    if (isNaN(pesos)) return 0;
    return Math.round(pesos * 100);
  }

  // ---- Cart state helpers -------------------------------------------------
  function getSubtotalCents() {
    var el = document.querySelector('.cart-drawer .dp-totals__value, .cart-drawer .totals__total-value');
    if (!el) return 0;
    return parseCents(el.textContent);
  }

  function setSubtotalCents(cents) {
    var el = document.querySelector('.cart-drawer .dp-totals__value, .cart-drawer .totals__total-value');
    if (!el) return;
    if (cents < 0) cents = 0;
    el.textContent = formatCents(cents);
  }

  function updateCartBubbleDelta(qtyDelta) {
    var bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
    if (!bubble) return;
    var current = parseInt(bubble.textContent, 10);
    if (isNaN(current)) return;
    var next = Math.max(0, current + qtyDelta);
    bubble.textContent = String(next);
  }

  // ---- Free-delivery progress bar -----------------------------------------
  function updateProgressBar(newSubtotalCents) {
    var bar = document.querySelector('#CartDrawer .dp-delivery-bar');
    if (!bar) return;

    var fill = bar.querySelector('.dp-delivery-bar__fill');
    var msg = bar.querySelector('.dp-delivery-bar__message');
    if (!fill || !msg) return;

    var pct = (newSubtotalCents / FREE_DELIVERY_THRESHOLD_CENTS) * 100;
    if (pct > 100) pct = 100;
    if (pct < 0) pct = 0;
    fill.style.width = pct + '%';

    var isQualified = newSubtotalCents >= FREE_DELIVERY_THRESHOLD_CENTS;
    var wasQualified = bar.classList.contains('dp-delivery-bar--qualified');

    if (isQualified && !wasQualified) {
      bar.classList.add('dp-delivery-bar--qualified');
      msg.innerHTML =
        '<span class="dp-delivery-bar__icon">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#b85c38" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>' +
        '</span>' +
        '<strong>You\'ve unlocked FREE delivery!</strong>';
    } else if (!isQualified && wasQualified) {
      // Dropped back below threshold. Rebuild the "add more" message.
      bar.classList.remove('dp-delivery-bar--qualified');
      var remaining = FREE_DELIVERY_THRESHOLD_CENTS - newSubtotalCents;
      msg.innerHTML =
        '<span class="dp-delivery-bar__icon">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1b3d61" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>' +
        '</span>' +
        'Add <strong>' + formatCents(remaining, { forceDecimals: false, withSuffix: false }) + '</strong> more for <strong>FREE delivery!</strong>';
    } else if (!isQualified) {
      // Still below — just update the remaining number in place.
      var remainingCents = FREE_DELIVERY_THRESHOLD_CENTS - newSubtotalCents;
      var strongs = msg.querySelectorAll('strong');
      if (strongs.length >= 1) {
        strongs[0].textContent = formatCents(remainingCents, { forceDecimals: false, withSuffix: false });
      }
    }
  }

  // ---- Line-level updates -------------------------------------------------
  function updateLineTotal(lineItem, newQty, unitPriceCents) {
    // Update the displayed line total in the cart-item__price-wrapper.
    // Prefer the regular price span; if there's a discounted price block,
    // update the "final" one only.
    var wrapper = lineItem.querySelector('.cart-item__price-wrapper');
    if (!wrapper) return;

    var target = wrapper.querySelector('.cart-item__discounted-prices .price--end:last-of-type')
              || wrapper.querySelector('.price.price--end');
    if (!target) return;

    var newLineTotal = newQty * unitPriceCents;
    target.textContent = formatCents(newLineTotal, { withSuffix: false });
  }

  // ---- Event handlers -----------------------------------------------------
  function handleQuantityButton(e) {
    var btn = e.target.closest('.quantity__button');
    if (!btn) return;
    if (!btn.closest('#CartDrawer, cart-drawer')) return; // drawer only

    // Respect Dawn's disabled states.
    if (btn.disabled || btn.classList.contains('disabled')) return;

    var action = btn.getAttribute('name'); // 'plus' | 'minus'
    if (action !== 'plus' && action !== 'minus') return;

    var lineItem = btn.closest('[data-dp-unit-price]');
    if (!lineItem) return;

    var input = lineItem.querySelector('.quantity__input');
    if (!input) return;

    var unitPrice = parseInt(lineItem.getAttribute('data-dp-unit-price'), 10) || 0;

    // Dawn's QuantityInput.onButtonClick runs in the same bubble phase and
    // directly updates input.value via stepUp()/stepDown(). We read the
    // new value from the DOM rather than guessing.
    //
    // Timing: both Dawn's handler and this one are attached directly via
    // addEventListener. Dawn's is attached in its custom element
    // constructor, which runs before document-level listeners in terms
    // of DOM hierarchy — but we run in bubble phase at the document level,
    // which is AFTER Dawn's per-button handler completes. So input.value
    // here reflects the post-step value.
    var newQty = parseInt(input.value, 10);
    if (isNaN(newQty)) return;

    // Visual: mark line as pending so CSS shows the subtle tint.
    lineItem.setAttribute('data-dp-pending', 'true');

    // Apply per-click delta (always ±1 for a button tap).
    var qtyDelta = action === 'plus' ? 1 : -1;
    var cashDelta = qtyDelta * unitPrice;

    // Line total reflects the absolute new qty.
    updateLineTotal(lineItem, newQty, unitPrice);

    // Subtotal + progress bar + cart bubble move by the delta.
    var newSubtotal = getSubtotalCents() + cashDelta;
    setSubtotalCents(newSubtotal);
    updateProgressBar(newSubtotal);
    updateCartBubbleDelta(qtyDelta);
  }

  function handleRemoveButton(e) {
    var btn = e.target.closest('cart-remove-button');
    if (!btn) return;
    if (!btn.closest('#CartDrawer, cart-drawer')) return;

    var lineItem = btn.closest('[data-dp-unit-price]');
    if (!lineItem) return;

    var input = lineItem.querySelector('.quantity__input');
    var currentQty = input ? parseInt(input.value, 10) : 0;
    if (isNaN(currentQty)) currentQty = 0;

    var unitPrice = parseInt(lineItem.getAttribute('data-dp-unit-price'), 10) || 0;
    var cashDelta = -unitPrice * currentQty;

    // Fade the line out; the server re-render will actually remove it.
    lineItem.setAttribute('data-dp-removing', 'true');

    var newSubtotal = getSubtotalCents() + cashDelta;
    setSubtotalCents(newSubtotal);
    updateProgressBar(newSubtotal);
    updateCartBubbleDelta(-currentQty);
  }

  // ---- Wiring -------------------------------------------------------------
  // Capture phase on document so we survive any drawer DOM replacement —
  // Dawn's cart.js swaps innerHTML, which would blow away handlers attached
  // to specific buttons. Delegation makes this robust.
  document.addEventListener('click', handleQuantityButton, false);
  document.addEventListener('click', handleRemoveButton, false);

  // When the real cart update settles, Dawn replaces the drawer innerHTML
  // with server-rendered markup. That already has the correct values and
  // clears any [data-dp-pending] / [data-dp-removing] flags via DOM reset.
  // Also reset the money format cache in case the shop config changed.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function() {
      _moneyFormatCache = null;
    });
  }
})();
